import { isBefore } from 'date-fns';
import React, { useEffect } from 'react';
import { Control, Controller, FieldErrors, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { MdCalendarToday, MdInfo } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { Checkbox, CheckboxGroup, DateRangePicker, SelectPicker, Toggle } from 'rsuite';
import { InputBox, Tooltips } from '../../../../../../../components';
import { optimizationPricesSelector } from '../../../../../../../reducers/OtimizacaoPrecos';
import { periodOptions } from '../../../../../../../reducers/OtimizacaoPrecos/optimizationPrices';
import { DatePickerCustom } from '../../../../components/DatePickerCustom/DatePickerCustom';
import { ShowErrors } from '../../../../components/ShowErrors';
import { useScheduleOptimizationPrices } from '../../../../hooks/useScheduleOptimizationPrices';
import { FormOptimizationPrices } from '../../../../types/schema.form';
import styles from './PeriodSection.module.scss';

const locale = {
  sunday: 'Dom',
  monday: 'Seg',
  tuesday: 'Ter',
  wednesday: 'Qua',
  thursday: 'Qui',
  friday: 'Sex',
  saturday: 'Sáb',
  ok: 'OK',
  today: 'Hoje',
  yesterday: 'Ontem',
  tomorrow: 'Amanhã',
  formattedMonthPattern: 'MMM yyyy',
  formattedDayPattern: 'DD/MM/YYYY',
  shortDateFormat: 'dd/MM/yyyy',
};

interface PeriodSectionProps {
  control: Control<FormOptimizationPrices>;
  watch: UseFormWatch<FormOptimizationPrices>;
  errors: FieldErrors<FormOptimizationPrices>;
  setValue: UseFormSetValue<FormOptimizationPrices>;
}

const { beforeToday } = DateRangePicker;
export const PeriodSection = ({ control, watch, errors, setValue }: PeriodSectionProps) => {
  const { scheduleOptimizationPrices } = useSelector(optimizationPricesSelector);
  const { handleSetOptions } = useScheduleOptimizationPrices();
  const { period } = scheduleOptimizationPrices;

  const periodType = watch('scheduleOptimizationPrices.period.value') || 'weekly';
  const isActiveEndDate = watch('scheduleOptimizationPrices.validity.end.active');
  const startDate = watch('scheduleOptimizationPrices.validity.start.startDate') || new Date();

  const activePeriod = period.find(item => item.value === periodType);

  const disabledPeriodAndOptions = true;

  useEffect(() => {
    setValue('scheduleOptimizationPrices.period.options', [...(activePeriod?.options ?? []).filter(item => item.value).map(item => item.value)]);
  }, [activePeriod, setValue]);

  return (
    <main className={styles.container}>
      <header className={styles.title}>
        <span>Defina quando recalcular os preços otimizados</span>
      </header>
      <section className={styles.content}>
        {/* periodo */}
        <section className={styles.box}>
          <header className={styles.title}>
            <span>período para recálculo</span>
            <p>Defina um prazo para refazer o cálculo de otimização dos preços. Para um melhor resultado, indicamos otimizar mensalmente.</p>
          </header>

          <section className={styles.body}>
            <section className={styles.inputs}>
              <div className={styles['input-group']}>
                <span>Período para nova otimização</span>

                <Controller
                  control={control}
                  name="scheduleOptimizationPrices.period.value"
                  render={({ field }) => {
                    return (
                      <SelectPicker
                        disabled={disabledPeriodAndOptions}
                        data={period}
                        value={field.value}
                        onSelect={value => field.onChange(value)}
                        searchable={false}
                        style={{ width: 296 }}
                        placeholder="Escolha uma opção"
                      />
                    );
                  }}
                />
              </div>

              <div className={styles['input-group']}>
                {activePeriod ? (
                  <span className={styles.info}>
                    {activePeriod.groupName}
                    <Tooltips.Info
                      color="#959595"
                      tooltip="O dia da semana reflete o dia da troca de preços nas lojas, as regras serão aplicadas um dia antes no IPA."
                    />
                  </span>
                ) : null}
                <InputBox>
                  <CheckboxGroup
                    name="checkbox-group"
                    inline
                    className={styles.weekdays}
                    value={activePeriod?.options?.filter(item => item.active).map(item => item.value) || []}
                    onChange={selectedValues => {
                      handleSetOptions(selectedValues);
                      setValue('scheduleOptimizationPrices.period.options', [...selectedValues]);
                    }}
                  >
                    {periodOptions[periodType]?.map(periodItem => {
                      return (
                        <Checkbox key={periodItem.value} value={periodItem.value} disabled={disabledPeriodAndOptions}>
                          {periodItem.label}
                        </Checkbox>
                      );
                    })}
                  </CheckboxGroup>
                  <ShowErrors message={errors.scheduleOptimizationPrices?.period?.options?.message} />
                </InputBox>
              </div>
            </section>
          </section>
        </section>

        {/* vigencia */}
        <section className={styles.box}>
          <header className={styles.title}>
            <span>vigência</span>
            {/* <p>Escolha quando iniciar a aplicação dos preços otimizados. Você também pode definir uma data final para a otimização. </p> */}
            <p>A aplicação de preços será iniciada automaticamente na data de criação. Você pode definir uma data final para a vigência, se desejar.</p>
          </header>

          <section className={styles.body}>
            <section className={styles.inputs}>
              {/* TODO - por enquanto a data de inicio vamos considerar apenas a data de criacao */}
              {/* <div className={styles['input-group']} style={{ minWidth: 184 }}>
                                <span>Início vigência</span>

                                <Controller
                                    control={control}
                                    name="scheduleOptimizationPrices.validity.start.startDate"
                                    render={({ field, fieldState: { error } }) => (
                                        <InputBox>
                                            <DatePickerCustom
                                                format="DD/MM/YYYY"
                                                placement="topStart"
                                                value={field.value}
                                                onChange={(value) => field.onChange(value)}
                                                oneTap
                                                ranges={[]}
                                                icon={<MdCalendarToday color="#959595" />}
                                                locale={locale}
                                                disabledDate={beforeToday()}
                                            />
                                            <ShowErrors message={error?.message} />
                                        </InputBox>
                                    )}
                                />
                            </div> */}

              <div className={styles['input-group']} style={{ minWidth: 184 }}>
                <div className={styles.info}>
                  <Controller
                    control={control}
                    name="scheduleOptimizationPrices.validity.end.active"
                    render={({ field }) => <Toggle size="sm" checked={field.value} onChange={value => field.onChange(value)} />}
                  />

                  <span>Final da vigência</span>
                  <Tooltips.Info color="#959595" tooltip="Habilite e defina uma vigência final caso opte por uma regra com prazo final" />
                </div>

                <Controller
                  control={control}
                  name="scheduleOptimizationPrices.validity.end.endDate"
                  render={({ field, fieldState: { error } }) => (
                    <InputBox>
                      <DatePickerCustom
                        style={{ color: '#959595' }}
                        disabled={!isActiveEndDate}
                        format="DD/MM/YYYY"
                        placeholder="Final da vigência"
                        placement="topStart"
                        value={field.value || undefined}
                        onChange={value => field.onChange(value)}
                        oneTap
                        icon={<MdCalendarToday color="#959595" />}
                        ranges={[]}
                        locale={locale}
                        disabledDate={date => {
                          if (!date) return false;
                          return isBefore(date, new Date(startDate));
                        }}
                      />
                      <ShowErrors message={error?.message} />
                    </InputBox>
                  )}
                />
              </div>
            </section>
          </section>
        </section>

        <div className={styles['info-calculate']}>
          <span>
            <MdInfo color="#378EF0" size={20} />
          </span>
          <p>
            O recálculo semanal será aplicado sempre às segundas-feiras. Como essa opção foi selecionada, o modelo estará vigente a partir da próxima
            terça-feira.
          </p>
        </div>
      </section>
    </main>
  );
};
